import { useState, useEffect } from 'react';

/**
 * Custom hook to detect online/offline network status
 *
 * @returns {boolean} isOnline - Current online status
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isOnline = useOnlineStatus();
 *   return <div>{isOnline ? 'Online' : 'Offline'}</div>;
 * }
 * ```
 *
 * Features:
 * - Monitors browser online/offline events
 * - Returns current network status
 * - Automatically updates on network changes
 * - Works with PWA offline mode
 *
 * Browser Support:
 * - Uses navigator.onLine API (widely supported)
 * - Listens to 'online' and 'offline' window events
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
